/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: BlacklistPage.tsx
 ??? Created: 2025-01-21
 ?? Description:
    Blacklist list page with table view, search, pagination, and actions.
    Allows viewing, creating, editing, duplicating, and deleting blacklist entries.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search, pagination, and sorting
    - Uses shared notification context and navigation store

 ?? Last Updated: 2025-11-08 by GPT-5 Codex
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ban, Copy, Edit, Trash2 } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { navigateToBlacklistDuplicate } from './blacklistActions';
import { deleteBlacklistEntry } from './api';
import { useBlacklistList } from './hooks';
import { BlacklistEntry } from './types';

export default function BlacklistPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();

  const pageTitle = useMemo(() => t('blacklist.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    entry: BlacklistEntry | null;
  }>({
    isOpen: false,
    entry: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);


  const availableColumns = useMemo(
    () => [
      {
        key: 'value',
        label: t('blacklist.fields.value.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: BlacklistEntry) => (
          <div className="space-y-1">
            <div className="font-medium text-foreground">{item.value}</div>
            {item.description ? (
              <div className="max-w-md truncate text-sm text-muted-foreground">
                {item.description}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'type',
        label: t('blacklist.fields.type.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: BlacklistEntry) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${item.type === 'number'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                : item.type === 'pattern'
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                  : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
              }`}
          >
            {t(`blacklist.types.${item.type}`)}
          </span>
        ),
      },
      {
        key: 'scope',
        label: t('blacklist.fields.scope.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: BlacklistEntry) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${item.scope === 'inbound'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : item.scope === 'outbound'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
              }`}
          >
            {t(`blacklist.scopes.${item.scope}`)}
          </span>
        ),
      },
      {
        key: 'description',
        label: t('blacklist.fields.description.label'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: BlacklistEntry) => (
          <span className="block max-w-md truncate text-sm text-muted-foreground">
            {item.description || '�'}
          </span>
        ),
      },
      {
        key: 'source',
        label: t('blacklist.fields.source.label'),
        visible: false,
        sortable: true,
        render: (_value: unknown, item: BlacklistEntry) => (
          <span className="text-sm text-muted-foreground">
            {t(`blacklist.sources.${item.source}`)}
          </span>
        ),
      },
      {
        key: 'enabled',
        label: t('blacklist.fields.enabled.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: BlacklistEntry) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${item.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
              }`}
          >
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'createdAt',
        label: t('common.createdAt'),
        visible: false,
        sortable: true,
        render: (_value: unknown, item: BlacklistEntry) => {
          if (!item.createdAt) {
            return <span className="text-sm text-muted-foreground">�</span>;
          }
          const createdDate = new Date(item.createdAt);
          if (Number.isNaN(createdDate.getTime())) {
            return <span className="text-sm text-muted-foreground">�</span>;
          }
          return (
            <span className="text-sm text-muted-foreground">
              {createdDate.toLocaleString()}
            </span>
          );
        },
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t],
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useBlacklistList({ initialColumns: availableColumns });

  const handleCreate = () => {
    navigate('/pbx/incoming-tools/blacklist/new');
  };

  const handleEdit = (entry: BlacklistEntry) => {
    navigate(`/pbx/incoming-tools/blacklist/${entry.uuid ?? entry.id}`);
  };

  const handleDuplicate = useCallback(
    (entry: BlacklistEntry) => {
      navigateToBlacklistDuplicate(navigate, entry.uuid ?? entry.id);
    },
    [navigate],
  );

  const handleDeleteClick = useCallback((entry: BlacklistEntry) => {
    setDeleteModal({ isOpen: true, entry });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.entry) return;

    setIsDeleting(true);
    try {
      await deleteBlacklistEntry(deleteModal.entry.id);

      showSuccess(t('blacklist.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, entry: null });
      refresh();
    } catch (error) {
      showError(error.message || t('blacklist.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.entry, refresh, showError, showSuccess, t]);

  const renderRowActions = useCallback(
    (entry: BlacklistEntry) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(entry)}
            aria-label={t('common.edit')}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(entry)}
            aria-label={t('common.duplicate')}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(entry)}
            aria-label={t('common.delete')}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onColumnToggle={handleColumnToggle}
          title={pageTitle}
          titleIcon={<Ban className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          searchPlaceholder={t('blacklist.searchPlaceholder')}
          renderActions={renderRowActions}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, entry: null })}
        onConfirm={handleDeleteConfirm}
        title={t('blacklist.messages.deleteTitle') || 'Delete Blacklist Entry'}
        message={t('blacklist.messages.confirmDelete', {
          value: deleteModal.entry?.value || '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

